import '@mui/x-tree-view/themeAugmentation'
import { Components, Theme } from '@mui/material'
import { ChevronDownOMIcon, ChevronRightOMIcon } from '@omnea-app/icons'

declare module '@mui/x-tree-view/TreeItem' {
  interface TreeItemProps {
    /** Marks a row whose record the viewer may not open; the row stops inviting a click. */
    'data-restricted'?: boolean
  }
}

const CHEVRON_SIZE_PX = 12

const TreeItemCollapseIcon = () => <ChevronDownOMIcon size={`${CHEVRON_SIZE_PX}px`} />

const TreeItemExpandIcon = () => <ChevronRightOMIcon size={`${CHEVRON_SIZE_PX}px`} />

export const MuiTreeItem: Components<Omit<Theme, 'components'>>['MuiTreeItem'] = {
  defaultProps: {
    slots: {
      collapseIcon: TreeItemCollapseIcon,
      expandIcon: TreeItemExpandIcon,
    },
  },
  variants: [
    {
      // A restricted node is shown by its position only and cannot be opened, so it must not invite a click.
      props: { 'data-restricted': true },
      style: {
        '& > .MuiTreeItem-content': {
          cursor: 'default',
          '&:hover .MuiTreeItem-label, &.Mui-focused .MuiTreeItem-label': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  ],
  styleOverrides: {
    content: ({ theme }) => ({
      padding: 0,
      marginBottom: theme.spacing(1),
      gap: theme.spacing(2),
      cursor: 'pointer',
      '&:hover, &.Mui-focused, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focused': {
        backgroundColor: 'transparent',
      },
      '&:hover .MuiTreeItem-label, &.Mui-focused .MuiTreeItem-label': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },
      '&.Mui-selected .MuiTreeItem-label': {
        backgroundColor: theme.palette.bg.neutral.muted,
      },
      '&.Mui-selected:hover .MuiTreeItem-label': {
        backgroundColor: theme.palette.bg.neutral.mutedHover,
      },
    }),
    label: ({ theme }) => ({
      padding: theme.spacing(1.5, 2.5),
      borderRadius: theme.shape['radius-md'],
    }),
    iconContainer: {
      width: `${CHEVRON_SIZE_PX}px`,
      height: `${CHEVRON_SIZE_PX}px`,
    },
    groupTransition: ({ theme }) => ({
      marginLeft: `calc(${CHEVRON_SIZE_PX / 2}px - ${theme.strokeWidth.strong} / 2)`,
      paddingLeft: theme.spacing(5),
      borderLeft: `${theme.strokeWidth.strong} solid ${theme.palette.border.subtle}`,
    }),
  },
}
